import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Chip, CircularProgress, Button,
  TextField, MenuItem, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import api from '../api';
import { conEmpresa } from '../empresa';
import { useSede } from '../contexto/Sede';
import { usePeriodo } from '../contexto/Periodo';
import { useEsMovil, tablaAncha, filtro } from '../estilos/responsivo';
import Tile, { REJILLA_TILES, pesos } from './Tile';
import { CONTENEDOR_TABLA } from '../estilos/tarjeta';

const categoriaLabel = {
  costo: { label: 'Costo', bg: '#fff7ed', color: '#ea580c' },
  gasto: { label: 'Gasto', bg: '#fef2f2', color: '#dc2626' },
};

const estadoLabel = {
  pendiente: { label: 'Pendiente', bg: '#fef3c7', color: '#d97706' },
  pagado: { label: 'Pagado', bg: '#d1fae5', color: '#059669' },
};

const metodoOpciones = [
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'debito_automatico', label: 'Debito automatico' },
];

export default function Egresos() {
  const esMovil = useEsMovil();
  const { params: paramsSede, nombre: nombreSede, sedeId } = useSede();
  const [conceptos, setConceptos] = useState([]);
  // El contador puede saltarse el catálogo y apuntar a una cuenta puntual.
  const [modoContable, setModoContable] = useState(false);
  const { periodo, esActual, enLetras: periodoLargo } = usePeriodo();
  const [data, setData] = useState({ egresos: [], total: 0, cantidad: 0 });
  const [kpis, setKpis] = useState(null);
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  // El período viene del menú lateral: se escoge una vez para toda la
  // aplicación en vez de repetirlo en cada pantalla.
  const filtroPeriodo = periodo;
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroProveedor, setFiltroProveedor] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    descripcion: '', categoria: '', concepto_id: '', cuenta_contable_id: '', monto: '',
    tercero_id: '', proveedor: '', numero_factura_proveedor: '', metodo_pago: 'transferencia',
    referencia: '', notas: '', estado: 'pendiente', periodo_fiscal: '',
  });
  const [saving, setSaving] = useState(false);
  const [proveedores, setProveedores] = useState([]);

  // Pagar dialog
  const [pagarDialogOpen, setPagarDialogOpen] = useState(false);
  const [pagarId, setPagarId] = useState(null);
  const [pagarForm, setPagarForm] = useState({ metodo_pago: 'transferencia', referencia: '' });

  const fetchData = async () => {
    try {
      const params = {};
      if (filtroPeriodo) params.periodo = filtroPeriodo;
      if (filtroCategoria) params.categoria = filtroCategoria;
      if (filtroEstado) params.estado = filtroEstado;
      if (filtroProveedor) params.proveedor = filtroProveedor;

      const [eRes, kRes, cRes, conceptosRes, pRes] = await Promise.all([
        api.get('/egresos', { params: { ...params, ...paramsSede } }),
        api.get('/dashboard/egresos', { params: { periodo: filtroPeriodo, ...paramsSede } }),
        api.get('/contabilidad/cuentas'),
        api.get('/conceptos', { params: { tipo: 'egreso' } }),
        api.get('/terceros', { params: { rol: 'proveedor' } }),
      ]);
      setData(eRes.data);
      setKpis(kRes.data);
      setProveedores(pRes.data || []);
      setConceptos(conceptosRes.data || []);
      // Cuentas de gasto (5xxx) y de costo (6xxx y 7xxx). El plan del
      // lavadero lleva los costos de operación en el 7, y sin esto no
      // aparecían en el desplegable.
      const cuentasGC = (cRes.data || []).filter(c =>
        ['5', '6', '7'].includes(String(c.codigo)[0])
      );
      setCuentas(cuentasGC);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroPeriodo, filtroCategoria, filtroEstado, filtroProveedor, sedeId]);

  const handleOpenNew = () => {
    setEditingId(null);
    setForm({
      descripcion: '', categoria: '', concepto_id: '', cuenta_contable_id: '', monto: '',
      tercero_id: '', proveedor: '', numero_factura_proveedor: '', metodo_pago: 'transferencia',
      referencia: '', notas: '', estado: 'pendiente', periodo_fiscal: filtroPeriodo,
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (egreso) => {
    setEditingId(egreso.id);
    setForm({
      descripcion: egreso.descripcion, categoria: egreso.categoria,
      cuenta_contable_id: egreso.cuenta_contable_id, concepto_id: egreso.concepto_id || '',
      monto: egreso.monto,
      tercero_id: egreso.tercero_id || '', proveedor: egreso.proveedor || '',
      numero_factura_proveedor: egreso.numero_factura_proveedor || '',
      metodo_pago: egreso.metodo_pago || 'transferencia', referencia: egreso.referencia || '',
      notas: egreso.notas || '', estado: egreso.estado, periodo_fiscal: egreso.periodo_fiscal,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.descripcion || !form.monto) return;
    if (!form.concepto_id && !form.cuenta_contable_id) return;
    setSaving(true);
    try {
      const payload = {
        ...form, monto: parseFloat(form.monto),
        concepto_id: form.concepto_id || null,
        cuenta_contable_id: form.cuenta_contable_id ? parseInt(form.cuenta_contable_id) : null,
        categoria: form.categoria || null,
        tercero_id: form.tercero_id ? parseInt(form.tercero_id) : null,
      };
      if (editingId) {
        await api.put(`/egresos/${editingId}`, payload);
      } else {
        await api.post('/egresos', payload);
      }
      setDialogOpen(false);
      setLoading(true);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este egreso?')) return;
    try {
      await api.delete(`/egresos/${id}`);
      setLoading(true);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error al eliminar'); }
  };

  const handlePagar = async () => {
    if (!pagarId) return;
    setSaving(true);
    try {
      await api.post(`/egresos/${pagarId}/pagar`, null, {
        params: { metodo_pago: pagarForm.metodo_pago, referencia: pagarForm.referencia || undefined }
      });
      setPagarDialogOpen(false);
      setLoading(true);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Error al marcar pagado'); }
    finally { setSaving(false); }
  };

  const handleExport = () => {
    const params = new URLSearchParams();
    if (filtroPeriodo) params.set('periodo', filtroPeriodo);
    if (filtroCategoria) params.set('categoria', filtroCategoria);
    if (filtroEstado) params.set('estado', filtroEstado);
    if (filtroProveedor) params.set('proveedor', filtroProveedor);
    window.open(conEmpresa(`${api.defaults.baseURL}/egresos/exportar-excel?${params.toString()}`), '_blank');
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography sx={{ color: '#64748b', fontSize: 13 }}>
            Costos y gastos — {data.cantidad} registro{data.cantidad !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<FileDownloadIcon />}
            onClick={handleExport}
            sx={{ borderColor: '#059669', color: '#059669', borderRadius: 1, textTransform: 'none', fontWeight: 600 }}>
            Excel
          </Button>
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={handleOpenNew}
            sx={{ bgcolor: '#1e3a5f', borderRadius: 1, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#2d5a87' } }}>
            Nuevo Egreso
          </Button>
        </Box>
      </Box>

      {/* KPIs */}
      {kpis && (
        <Box sx={{ ...REJILLA_TILES, mb: 1 }}>
          <Tile label="Costos del mes"    v={kpis.total_costos}    color="#c2410c" bg="#ffedd5" />
          <Tile label="Gastos del mes"    v={kpis.total_gastos}    color="#b91c1c" bg="#fee2e2" />
          <Tile label="Pendiente de pago" v={kpis.total_pendiente} color="#a16207" bg="#fef3c7" />
          <Tile label="Pagado del mes"    v={kpis.total_pagado}    color="#059669" bg="#d1fae5" />
        </Box>
      )}

      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField select label="Categoria" value={filtroCategoria}
          onChange={(e) => setFiltroCategoria(e.target.value)} size="small" sx={filtro(140)}>
          <MenuItem value="">Todas</MenuItem>
          <MenuItem value="costo">Costo</MenuItem>
          <MenuItem value="gasto">Gasto</MenuItem>
        </TextField>
        <TextField select label="Estado" value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value)} size="small" sx={filtro(140)}>
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="pendiente">Pendiente</MenuItem>
          <MenuItem value="pagado">Pagado</MenuItem>
        </TextField>
        <TextField
          label="Proveedor" value={filtroProveedor}
          onChange={(e) => setFiltroProveedor(e.target.value)}
          size="small" sx={filtro(180)}
          placeholder="Buscar proveedor..."
        />
      </Box>

      {/* Tabla */}
      <Paper elevation={0} sx={CONTENEDOR_TABLA}>
        {data.egresos.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1.5 }} />
            <Typography sx={{ color: '#1e293b', fontSize: 16, fontWeight: 600 }}>No hay egresos registrados</Typography>
            <Typography sx={{ color: '#64748b', fontSize: 13 }}>Registra costos y gastos con el boton "Nuevo Egreso"</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table sx={tablaAncha(900)}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>FECHA</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>PROVEEDOR</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>DESCRIPCION</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>CATEGORIA</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>CUENTA</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }} align="right">MONTO</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }}>ESTADO</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#64748b', fontSize: 12 }} align="center">ACCIONES</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.egresos.map((e) => {
                  const cl = categoriaLabel[e.categoria] || categoriaLabel.costo;
                  const el = estadoLabel[e.estado] || estadoLabel.pendiente;
                  const fecha = e.fecha ? new Date(e.fecha).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
                  return (
                    <TableRow key={e.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                      <TableCell sx={{ fontSize: 13, color: '#1e293b' }}>{fecha}</TableCell>
                      <TableCell sx={{ fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{e.tercero_nombre || e.proveedor || '-'}</TableCell>
                      <TableCell sx={{ fontSize: 13, color: '#1e293b', maxWidth: 200 }}>
                        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {e.descripcion}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={cl.label} size="small" sx={{ height: 22, fontSize: 10, fontWeight: 600, bgcolor: cl.bg, color: cl.color }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: '#64748b' }}>
                        {e.cuenta_codigo} {e.cuenta_nombre}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700, color: '#dc2626' }}>
                        {pesos(e.monto)}
                      </TableCell>
                      <TableCell>
                        <Chip label={el.label} size="small" sx={{ height: 22, fontSize: 10, fontWeight: 600, bgcolor: el.bg, color: el.color }} />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          {e.estado === 'pendiente' && (
                            <>
                              <Tooltip title="Marcar pagado">
                                <IconButton size="small" onClick={() => {
                                  setPagarId(e.id);
                                  setPagarForm({ metodo_pago: e.metodo_pago || 'transferencia', referencia: '' });
                                  setPagarDialogOpen(true);
                                }}
                                  sx={{ color: '#059669' }}>
                                  <CheckCircleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Editar">
                                <IconButton size="small" onClick={() => handleOpenEdit(e)} sx={{ color: '#2563eb' }}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar">
                                <IconButton size="small" onClick={() => handleDelete(e.id)} sx={{ color: '#dc2626' }}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {e.estado === 'pagado' && (
                            <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>—</Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialog: Crear/Editar Egreso */}
      <Dialog fullScreen={esMovil} open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle>
          {editingId ? 'Editar Egreso' : 'Nuevo Egreso'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Descripcion" value={form.descripcion} required
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              placeholder="Ej: Factura OpenAI Febrero 2026" size="small" />
            {/* El concepto en lenguaje de negocio. Trae consigo la cuenta
                contable y si es costo o gasto, así que quien registra el
                egreso no tiene que saber contabilidad. */}
            <TextField select label="¿Qué se pagó?" value={form.concepto_id || ''} required
              onChange={(e) => {
                const c = conceptos.find(x => x.id === e.target.value);
                setForm({ ...form, concepto_id: e.target.value,
                  categoria: c?.categoria || form.categoria,
                  cuenta_contable_id: c?.cuenta_contable_id || '' });
              }} size="small"
              helperText={(() => {
                const c = conceptos.find(x => x.id === form.concepto_id);
                if (!c) return 'Escoge el tipo de gasto; la contabilidad se arma sola';
                if (c.requiere_revision) return 'Se registra y queda marcado para que contabilidad lo clasifique';
                return `Se registra como ${c.categoria} en ${c.cuenta_codigo} ${c.cuenta_nombre}`;
              })()}>
              <MenuItem value="">Seleccionar...</MenuItem>
              {conceptos.map(c => (
                <MenuItem key={c.id} value={c.id}>
                  {c.icono ? `${c.icono}  ` : ''}{c.nombre}
                </MenuItem>
              ))}
            </TextField>

            {!editingId && (
              <TextField select label="Estado" value={form.estado}
                onChange={(e) => setForm({ ...form, estado: e.target.value })} size="small">
                <MenuItem value="pendiente">Pendiente</MenuItem>
                <MenuItem value="pagado">Ya pagado</MenuItem>
              </TextField>
            )}

            {/* Salida para quien sí sabe: apuntar a una cuenta puntual sin
                pasar por el catálogo. Oculta por defecto para no confundir
                a quien no la necesita. */}
            <Box>
              <Button size="small" onClick={() => setModoContable(v => !v)}
                sx={{ textTransform: 'none', fontSize: 12, p: 0, minWidth: 0 }}>
                {modoContable ? '− Ocultar detalle contable' : '+ Escoger la cuenta contable a mano'}
              </Button>
            </Box>
            {modoContable && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField select label="Categoria" value={form.categoria}
                  onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                  size="small" sx={{ flex: 1 }}>
                  <MenuItem value="costo">Costo (produccion)</MenuItem>
                  <MenuItem value="gasto">Gasto (operativo)</MenuItem>
                </TextField>
                <TextField select label="Cuenta contable" value={form.cuenta_contable_id}
                  onChange={(e) => setForm({ ...form, cuenta_contable_id: e.target.value })}
                  size="small" sx={{ flex: 2 }}
                  helperText="Vacío = la del concepto">
                  <MenuItem value="">La del concepto</MenuItem>
                  {cuentas.map(c => (
                    <MenuItem key={c.id} value={c.id}>{c.codigo} - {c.nombre}</MenuItem>
                  ))}
                </TextField>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Monto" type="number" value={form.monto} required
                onChange={(e) => setForm({ ...form, monto: e.target.value })}
                size="small" sx={{ flex: 1 }} InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: '#94a3b8' }}>$</Typography> }} />
              {/* El período viene del menú. Se deja visible y editable porque
                  fechar un documento tiene consecuencias: si el período activo
                  no es el mes en curso, hay que verlo, no descubrirlo después. */}
              <TextField label="Periodo fiscal" type="month" value={form.periodo_fiscal}
                onChange={(e) => setForm({ ...form, periodo_fiscal: e.target.value })}
                size="small" sx={{ flex: 1 }} InputLabelProps={{ shrink: true }}
                helperText={esActual ? 'Del menú' : `⚠ ${periodoLargo} no es el mes actual`}
                FormHelperTextProps={{ sx: { color: esActual ? undefined : '#b45309' } }} />
            </Box>
            <TextField select label="Proveedor (tercero)" value={form.tercero_id}
              onChange={(e) => {
                const id = e.target.value;
                const prov = proveedores.find(p => p.id === id);
                setForm({ ...form, tercero_id: id, proveedor: prov ? prov.razon_social : form.proveedor });
              }}
              size="small" helperText="Selecciona un tercero registrado o escribe abajo">
              <MenuItem value="">— Sin tercero —</MenuItem>
              {proveedores.map(p => (
                <MenuItem key={p.id} value={p.id}>{p.razon_social} ({p.tipo_documento}: {p.numero_documento})</MenuItem>
              ))}
            </TextField>
            <TextField label="Proveedor (texto libre)" value={form.proveedor}
              onChange={(e) => setForm({ ...form, proveedor: e.target.value })}
              placeholder="Solo si no esta registrado como tercero" size="small"
              disabled={!!form.tercero_id} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Factura proveedor #" value={form.numero_factura_proveedor}
                onChange={(e) => setForm({ ...form, numero_factura_proveedor: e.target.value })}
                size="small" sx={{ flex: 1 }} />
              <TextField select label="Metodo pago" value={form.metodo_pago}
                onChange={(e) => setForm({ ...form, metodo_pago: e.target.value })} size="small" sx={{ flex: 1 }}>
                {metodoOpciones.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </TextField>
            </Box>
            <TextField label="Referencia bancaria" value={form.referencia}
              onChange={(e) => setForm({ ...form, referencia: e.target.value })} size="small" />
            <TextField label="Notas" value={form.notas} multiline rows={2}
              onChange={(e) => setForm({ ...form, notas: e.target.value })} size="small" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#1e3a5f', textTransform: 'none', fontWeight: 600, borderRadius: 1, '&:hover': { bgcolor: '#2d5a87' } }}>
            {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear Egreso'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Marcar Pagado */}
      <Dialog fullScreen={esMovil} open={pagarDialogOpen} onClose={() => setPagarDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle>Marcar como Pagado</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField select label="Metodo de pago" value={pagarForm.metodo_pago}
              onChange={(e) => setPagarForm({ ...pagarForm, metodo_pago: e.target.value })} size="small">
              {metodoOpciones.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </TextField>
            <TextField label="Referencia bancaria" value={pagarForm.referencia}
              onChange={(e) => setPagarForm({ ...pagarForm, referencia: e.target.value })} size="small" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPagarDialogOpen(false)} sx={{ textTransform: 'none', color: '#64748b' }}>Cancelar</Button>
          <Button variant="contained" onClick={handlePagar} disabled={saving}
            sx={{ bgcolor: '#059669', textTransform: 'none', fontWeight: 600, borderRadius: 1, '&:hover': { bgcolor: '#047857' } }}>
            {saving ? 'Procesando...' : 'Confirmar Pago'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
